import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Receipt, AlertTriangle, ArrowUpRight, Clock } from "lucide-react";

function fmtMoney(cents) {
    if (cents == null) return "$0.00";
    const sign = cents < 0 ? "-" : "";
    const v = Math.abs(cents) / 100;
    return `${sign}$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * Phase J.2 Pass 2 — outstanding-invoices aging card shown on the admin dashboard.
 * Pulls /admin/invoices/aging-summary; refreshes on mount.
 */
export function InvoiceAgingCard() {
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/admin/invoices/aging-summary");
                setSummary(data);
            } catch {
                // best-effort widget — quietly fall back to empty state
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const overdueCount = summary?.overdue_count ?? 0;
    const accent = overdueCount > 0 ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700";

    return (
        <Card data-testid="invoice-aging-card" className="h-full">
            <CardHeader className="flex flex-row items-start justify-between gap-4 pb-2">
                <div className="space-y-1.5">
                    <CardTitle className="text-base font-semibold text-slate-900">Outstanding invoices</CardTitle>
                    <CardDescription className="text-sm text-slate-500">
                        Sent, viewed, and overdue invoices across all customers.
                    </CardDescription>
                </div>
                <span className={`inline-flex h-10 w-10 items-center justify-center rounded-md ${accent}`}>
                    <Receipt size={18} />
                </span>
            </CardHeader>
            <CardContent className="space-y-4">
                <div className="flex items-baseline justify-between">
                    <span
                        className="font-display text-3xl font-bold tracking-tight text-slate-900 tabular-nums"
                        data-testid="aging-outstanding-total"
                    >
                        {loading ? "…" : fmtMoney(summary?.outstanding_cents ?? 0)}
                    </span>
                    <span className="text-sm text-slate-500" data-testid="aging-outstanding-count">
                        {loading ? "—" : `${summary?.outstanding_count ?? 0} invoice${(summary?.outstanding_count ?? 0) === 1 ? "" : "s"}`}
                    </span>
                </div>

                <div className="grid grid-cols-3 gap-2 text-xs">
                    <div className="rounded-md border border-slate-200 p-2.5">
                        <div className="flex items-center gap-1.5 text-slate-500">
                            <Clock size={12} /> Due ≤ 7 d
                        </div>
                        <div className="mt-1 font-display text-lg font-bold text-slate-900 tabular-nums"
                             data-testid="aging-due-7">
                            {loading ? "—" : summary?.due_within_7_count ?? 0}
                        </div>
                    </div>
                    <div className="rounded-md border border-red-200 bg-red-50/40 p-2.5">
                        <div className="flex items-center gap-1.5 text-red-700">
                            <AlertTriangle size={12} /> Overdue
                        </div>
                        <div className="mt-1 font-display text-lg font-bold text-red-700 tabular-nums"
                             data-testid="aging-overdue">
                            {loading ? "—" : overdueCount}
                        </div>
                    </div>
                    <div className="rounded-md border border-amber-200 bg-amber-50/40 p-2.5">
                        <div className="flex items-center gap-1.5 text-amber-800">
                            <AlertTriangle size={12} /> 30+ days
                        </div>
                        <div className="mt-1 font-display text-lg font-bold text-amber-800 tabular-nums"
                             data-testid="aging-past-30">
                            {loading ? "—" : summary?.past_30_count ?? 0}
                        </div>
                    </div>
                </div>

                <div className="flex justify-end">
                    <Button asChild size="sm" variant="ghost" className="gap-1.5 text-blue-900 hover:text-blue-800">
                        <Link to="/admin/invoices?status=overdue" data-testid="aging-view-all">
                            View overdue <ArrowUpRight size={14} />
                        </Link>
                    </Button>
                </div>
            </CardContent>
        </Card>
    );
}

export default InvoiceAgingCard;
